package com.siriusxm.pia.rest.unifiedaggregator


import unifiedcontent.aggregator.*
import contentingestion.unifiedmodel.EntityType
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject
import kotlin.time.Instant

/**
 * AggregatorSDKClient wraps the new Aggregator SDK,
 * but falls back to the base UnifiedAggregatorClient for unimplemented methods.
 *
 * Everything here should be considered
 */
class AggregatorSDKClient(
    val sdk: Aggregator,
) : UnifiedAggregatorClient {

    override suspend fun fetchEntity(
        id: String,
        type: String,
        version: String
    ): JsonObject {
        val result = sdk.getEntity(id, type, version)
        return result.jsonObject
    }

    override suspend fun fetchEntityById(id: String, includeRemoved: String?): List<JsonObject> {
        return sdk.getEntityById(id).mapNotNull { it.jsonObjectOrNull() }
    }

    override suspend fun incomingEntity(
        id: String,
        type: String,
        version: String,
        fields: String?
    ): IncomingEntityResult {
        val entityType = EntityType.fromName(type)
        val result = sdk.getIncomingEntity(id, entityType, version)
        return IncomingEntityResult(
            entity = IncomingEvent(
                ts = result.entity.ts,
                auditContext = result.entity.auditContext,
                source = result.entity.source,
                data = result.entity.data.jsonObject,
            ),
            partials = result.partials?.map {
                IncomingEvent(
                    ts = it.ts,
                    source = it.source,
                    data = it.data.jsonObject,
                    partialId = it.partialId,
                    operations = it.operations,
                    auditContext = it.auditContext,
                )
            },
            scheduled = result.scheduled,
        )
    }

    override suspend fun outgoing(
        id: String,
        type: com.siriusxm.pia.rest.unifiedaggregator.EntityType,
        version: String
    ): JsonObject {
        val entityType = EntityType.fromName(type)
        return sdk.getOutgoingEntityByVersion(id, entityType, version).jsonObject
    }

    override suspend fun getOutgoingEntity(id: String): JsonArray {
        return JsonArray(sdk.getOutgoingEntity(id))
    }

    override suspend fun republish(id: String, type: String, version: String, updateTimestamp: Boolean) {
        sdk.republishEntity(
            entityId = id,
            entityType = EntityType.fromName(type),
            entityVersion = version,
            action = RepublishAction.republish,
            updateTimeStamp = updateTimestamp
        )
    }

    override suspend fun searchEntities(
        queryTerm: String?,
        types: String?,
        from: Int?,
        size: Int?,
        visible: Boolean?
    ): EntityListResponse {
        val sdkResponse = sdk.searchEntities(
            query = queryTerm,
            types = types,
            from = from,
            size = size,
            visible = visible
        )
        return EntityListResponse(
            entities = sdkResponse.entities.mapNotNull { it as? JsonObject },
            paginationToken = sdkResponse.paginationToken
        )
    }

    override suspend fun lookupSourceId(id: String): MappingResponse {
        val sdkResponse = sdk.getMapping(id)
        return MappingResponse(
            sourceId = sdkResponse.sourceId,
            atlasId = sdkResponse.atlasId
        )
    }

    override suspend fun serviceRoles(): ServiceUserMembershipList {
        return sdk.getUserRoles()
    }

    override suspend fun userPermissions(): UserPermissions {
        return sdk.getUserPermissions()
    }

    override suspend fun newProducer(producerDetails: ProducerDetails) {
        sdk.addProducer(producerDetails)
    }

    override suspend fun internalProducerUsers(producerId: String): ServiceUsersList {
        return sdk.getProducerUsers(producerId)
    }

    override suspend fun internalConsumerUsers(consumerId: String): ServiceUsersList {
        return sdk.getConsumerUsers(consumerId)
    }

    override suspend fun newConsumer(consumerDetails: ConsumerDetails) {
        sdk.addConsumer(consumerDetails)
    }

    override suspend fun relationshipSchemas(): List<RelationshipSchema> {
        return sdk.getRelationshipSchemas().map { it.toUi() }
    }

    override suspend fun relationshipSubjects(
        relationshipSchemaId: String,
        id: String,
        descending: Boolean?,
        limit: Int?,
        paginationToken: String?
    ): EntityListResponse {
        val sdkResponse = sdk.getRelationshipSubjects(
            relationshipId = relationshipSchemaId,
            entityId = id,
            descending = descending,
            limit = limit,
            paginationToken = paginationToken
        )
        return EntityListResponse(
            entities = sdkResponse.entities.mapNotNull { it as? JsonObject },
            paginationToken = sdkResponse.paginationToken
        )
    }

    override suspend fun relationshipObjects(
        relationshipSchemaId: String,
        id: String,
        descending: Boolean?,
        limit: Int?,
        paginationToken: String?
    ): EntityListResponse {
        val sdkResponse = sdk.getRelationshipObjects(
            relationshipId = relationshipSchemaId,
            entityId = id,
            descending = descending,
            limit = limit,
            paginationToken = paginationToken
        )
        return EntityListResponse(
            entities = sdkResponse.entities.mapNotNull { it as? JsonObject },
            paginationToken = sdkResponse.paginationToken
        )
    }

    override suspend fun entityTypeEvents(
        type: String,
        queryId: String?,
        startTime: Instant?,
        endTime: Instant?,
        option: EventQueryOption?,
        limit: Int?
    ): EventSearchStatus {
        val optionsList = option?.let { listOf(it) }

        return sdk.getEntityTypeEvents(
            entityType = EntityType.fromName(type),
            start = startTime,
            end = endTime,
            limit = limit,
            queryId = queryId,
            options = optionsList
        )
    }

    override suspend fun submitRelationship(
        relationshipSchemaId: String,
        update: IncomingRelationshipUpdate
    ) {
        val sdkUpdate = unifiedcontent.aggregator.IncomingRelationshipUpdate(
            subject = update.subject,
            `object` = update.`object`,
            data = update.data
        )
        sdk.submitRelationship(relationshipSchemaId, sdkUpdate)
    }

    override suspend fun deleteScheduledPartial(
        entityId: String,
        entityType: String,
        entityVersion: String,
        partialId: String,
        timestamp: String
    ) {
        sdk.deleteScheduledPartial(
            entityId = entityId,
            entityType = EntityType.fromName(entityType),
            entityVersion = entityVersion,
            partialId = partialId,
            ts = Instant.parse(timestamp)
        )
    }

    override suspend fun deleteScheduledUpdate(
        entityId: String,
        entityType: String,
        entityVersion: String,
        timestamp: String
    ) {
        sdk.deleteScheduledUpdate(
            entityId = entityId,
            entityType = EntityType.fromName(entityType),
            entityVersion = entityVersion,
            ts = Instant.parse(timestamp)
        )
    }

    override suspend fun internalConsumer(consumerId: String): ConsumerDetails {
        return sdk.getConsumer(consumerId)
    }

    override suspend fun internalAddTarget(
        consumerId: String,
        request: AddConsumerTarget
    ): ConsumerDetails {
        sdk.createConsumerTarget(consumerId, request)
        return sdk.getConsumer(consumerId)
    }

}